const pad = (n: number) => String(n).padStart(2, '0');

/** How long ago, as the feed's Age column writes it: "45s", "12m 0s", "3h 5m", "2d 4h". "—" when unknown or in the future. */
export function formatAge(fromMs: number | null, nowMs: number): string {
  if (fromMs == null || fromMs > nowMs) return '—';
  const totalSec = Math.max(0, Math.floor((nowMs - fromMs) / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin < 60) return `${totalMin}m ${totalSec % 60}s`;
  const totalHours = Math.floor(totalMin / 60);
  if (totalHours < 24) return `${totalHours}h ${totalMin % 60}m`;
  return `${Math.floor(totalHours / 24)}d ${totalHours % 24}h`;
}

type TimeInput = Date | string | number | null | undefined;

function toDate(value: TimeInput): Date | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * 24 h clock in UTC, "14:32". The zone is always written next to it by the caller
 * (docs/c3/DESIGN.md, Time). It is for a clock face or an axis tick; a moment is written
 * with `formatUtcDateTime` or `formatUtcTime`.
 */
export function formatUtcClock(date: Date): string {
  return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

/** "14:32:07 UTC" (or "14:32 UTC"), for a moment on a day the screen already names. "—" when there is none. */
export function formatUtcTime(value: TimeInput, seconds = true): string {
  const date = toDate(value);
  if (!date) return '—';
  const clock = `${formatUtcClock(date)}${seconds ? `:${pad(date.getUTCSeconds())}` : ''}`;
  return `${clock} UTC`;
}

/**
 * "2026-10-28 14:32 UTC" (or with seconds), the one way the console writes a moment (item 76): 24 h,
 * the zone always visible, never the reader's local time. "—" when there is none or it is not a date.
 */
export function formatUtcDateTime(value: TimeInput, seconds = false): string {
  const date = toDate(value);
  if (!date) return '—';
  const day = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  return `${day} ${formatUtcTime(date, seconds)}`;
}
